(function () {
  const form = document.querySelector('.search-form-large');
  const clearButton = form?.querySelector('[data-clear-input]');
  const input = form?.querySelector('input[type="search"]');
  if (!clearButton || !input) return;

  clearButton.addEventListener('click', () => {
    input.value = '';
    input.focus();
  });
})();

if (!customElements.get('search-results')) {
  class SearchResults extends HTMLElement {
    constructor() {
      super();
      this.typeTabs = [];
      this.abortController = null;
      this._filterAppliedUnsub = null;
      this._filterClearedUnsub = null;
      this._sortChangedUnsub = null;
      this._gridViewUnsub = null;
      this.currentGridView = null;
      this._appliedGridView = null;
      this.handleTabClick = this.handleTabClick.bind(this);
      this.handleGridViewChange = this.handleGridViewChange.bind(this);
    }

    connectedCallback() {
      this.typeTabs = [...this.querySelectorAll('[data-type-tab]')];
      this.typeTabs.forEach((tab) => tab.addEventListener('click', this.handleTabClick));

      this.onPopState = () => this.fetchAndUpdate(window.location.href, { fromPopState: true });
      window.addEventListener('popstate', this.onPopState);

      if (window.theme?.pubsub) {
        const events = theme.pubsub.events;
        this._filterAppliedUnsub = theme.pubsub.subscribe(events.FILTER_APPLIED, ({ url }) => {
          theme.pubsub.publish(events.SEARCH_RESULTS, { url, type: 'filter' });
        });
        this._filterClearedUnsub = theme.pubsub.subscribe(events.FILTER_CLEARED, ({ url }) => {
          theme.pubsub.publish(events.SEARCH_RESULTS, { url, type: 'filter-clear' });
        });
        this._sortChangedUnsub = theme.pubsub.subscribe(events.SORT_CHANGED, ({ url }) => {
          theme.pubsub.publish(events.SEARCH_RESULTS, { url, type: 'sort' });
        });
      }

      this.initGridViewListener();
    }

    disconnectedCallback() {
      this.typeTabs.forEach((tab) => tab.removeEventListener('click', this.handleTabClick));
      window.removeEventListener('popstate', this.onPopState);
      this.abortController?.abort();
      this._filterAppliedUnsub?.();
      this._filterClearedUnsub?.();
      this._sortChangedUnsub?.();
      this._gridViewUnsub?.();
      document.removeEventListener('collection-grid-view:change', this.handleGridViewChange);
    }

    initGridViewListener() {
      const viewSelector = document.querySelector('[data-grid-view-selector]');
      if (!viewSelector) return;

      if (window.theme?.pubsub) {
        this._gridViewUnsub = theme.pubsub.subscribe(theme.pubsub.events.GRID_VIEW_CHANGE, this.handleGridViewChange);
      } else {
        document.addEventListener('collection-grid-view:change', this.handleGridViewChange);
      }

      const defaultView = viewSelector.closest('collection-filters')?.dataset.defaultGridView || '4';
      const storedView = localStorage.getItem('collection-grid-view');
      const savedView = this.normalizeGridView(storedView, defaultView);
      if (storedView && storedView !== savedView) {
        localStorage.setItem('collection-grid-view', savedView);
      }
      this.currentGridView = savedView;

      const grid = this.querySelector('#search-results-grid');
      const alreadyApplied = grid?.classList.contains(`collection__grid--view-${savedView}`);
      this._appliedGridView = alreadyApplied ? savedView : null;
      if (!alreadyApplied) this.updateGridView(savedView);
    }

    normalizeGridView(rawView, fallbackView) {
      if (rawView === '2' || rawView === '4' || rawView === '5') return rawView;
      if (rawView === '12') return '5';
      return fallbackView;
    }

    handleGridViewChange(event) {
      const view = event.detail?.view || event.view;
      if (view) this.updateGridView(view);
    }

    updateGridView(view) {
      if (view === this._appliedGridView) return;

      const grid = this.querySelector('#search-results-grid');
      if (!grid) return;

      this.currentGridView = view;
      this._appliedGridView = view;

      const applyView = () => this.applyGridView(grid, view);
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (document.startViewTransition && !prefersReducedMotion) {
        document.startViewTransition(applyView);
      } else {
        applyView();
      }
    }

    applyGridView(grid, view) {
      grid.classList.remove('collection__grid--view-2', 'collection__grid--view-4', 'collection__grid--view-5');
      grid.classList.add(`collection__grid--view-${view}`);

      if (view === '5') {
        grid.style.setProperty('--columns-mobile', '3');
        grid.style.setProperty('--columns-tablet', '4');
        grid.style.setProperty('--columns-desktop', '5');
      } else if (view === '2') {
        grid.style.setProperty('--columns-mobile', '1');
        grid.style.setProperty('--columns-tablet', '2');
        grid.style.setProperty('--columns-desktop', '2');
      } else if (view === '4') {
        grid.style.setProperty('--columns-mobile', '2');
        grid.style.setProperty('--columns-tablet', '3');
        grid.style.setProperty('--columns-desktop', '4');
      }
    }

    handleTabClick(e) {
      e.preventDefault();
      const tab = e.currentTarget;
      const targetUrl = tab.href;
      this.fetchAndUpdate(targetUrl);
    }

    async fetchAndUpdate(targetUrl, { fromPopState = false } = {}) {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.setLoading(true);

      try {
        const response = await fetch(targetUrl, { signal });
        const html = await response.text();
        const doc = theme.utils.domParser.parseFromString(html, 'text/html');

        this.updateGrid(doc);
        this.updatePagination(doc);
        this.updateTypeTabs(doc);
        this.updateResultsCount(doc);
        this.updateFilters(doc);

        if (this.currentGridView) this.updateGridView(this.currentGridView);

        if (!fromPopState) window.history.pushState({}, '', targetUrl);

        if (window.theme?.pubsub) {
          theme.pubsub.publish(theme.pubsub.events.SEARCH_RESULTS, { url: targetUrl, type: 'type-filter' });
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.href = targetUrl;
      } finally {
        if (!signal.aborted) {
          this.abortController = null;
          this.setLoading(false);
        }
      }
    }

    updateGrid(doc) {
      const newGrid = doc.querySelector('#search-results-grid');
      const currentGrid = this.querySelector('#search-results-grid');
      if (newGrid && currentGrid) currentGrid.innerHTML = newGrid.innerHTML;
    }

    updatePagination(doc) {
      const newPagination = doc.querySelector('.search-results__pagination');
      const currentPagination = this.querySelector('.search-results__pagination');
      if (newPagination && currentPagination) currentPagination.innerHTML = newPagination.innerHTML;
    }

    updateTypeTabs(doc) {
      const newTabList = doc.querySelector('[data-type-tablist]');
      const currentTabList = this.querySelector('[data-type-tablist]');
      if (newTabList && currentTabList) {
        currentTabList.innerHTML = newTabList.innerHTML;
        this.typeTabs = [...this.querySelectorAll('[data-type-tab]')];
        this.typeTabs.forEach((tab) => tab.addEventListener('click', this.handleTabClick));
      }
    }

    updateResultsCount(doc) {
      const newCount = doc.querySelector('[data-results-count]');
      const currentCount = document.querySelector('[data-results-count]');
      if (newCount && currentCount) currentCount.innerHTML = newCount.innerHTML;
    }

    updateFilters(doc) {
      const newDrawerContent = doc.querySelector('[data-filter-drawer-content]');
      const currentDrawerContent = document.querySelector('[data-filter-drawer-content]');
      if (newDrawerContent && currentDrawerContent) currentDrawerContent.innerHTML = newDrawerContent.innerHTML;

      const newActiveFilters = doc.querySelector('[data-active-filters]');
      const currentActiveFilters = document.querySelector('[data-active-filters]');
      if (newActiveFilters && currentActiveFilters) {
        currentActiveFilters.innerHTML = newActiveFilters.innerHTML;
      }

      const newApplyCount = doc.querySelector('[data-filter-apply-count]');
      const currentApplyCount = document.querySelector('[data-filter-apply-count]');
      if (newApplyCount && currentApplyCount) currentApplyCount.textContent = newApplyCount.textContent.trim();
    }

    setLoading(show) {
      const grid = this.querySelector('#search-results-grid');
      if (!grid) return;

      if (show) {
        grid.style.opacity = '0.5';
        grid.style.pointerEvents = 'none';
        grid.style.transition = 'opacity 0.2s ease';
      } else {
        grid.style.opacity = '';
        grid.style.pointerEvents = '';
        grid.style.transition = '';
      }
    }
  }

  customElements.define('search-results', SearchResults);
}

if (!customElements.get('search-recently-viewed')) {
  class SearchRecentlyViewed extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
    }

    connectedCallback() {
      const handles = window.theme?.recentlyViewed?.getHandles?.() || [];
      if (handles.length === 0) return;

      this.load(handles.slice(0, 4));
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    async load(handles) {
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      const query = handles.map((h) => `handle:${h}`).join(' OR ');
      const url = `${this.dataset.searchUrl}?type=product&q=${encodeURIComponent(query)}`;

      try {
        const response = await fetch(url, { signal });
        const html = await response.text();
        const doc = theme.utils.domParser.parseFromString(html, 'text/html');

        const items = [...doc.querySelectorAll('#search-results-grid .grid__item')];
        if (items.length === 0) return;

        const list = this.querySelector('[data-search-recently-viewed-list]');
        if (!list) return;

        items.forEach((item) => list.appendChild(item));
        this.removeAttribute('hidden');
      } catch (error) {
        if (error.name === 'AbortError') return;
      } finally {
        if (!signal.aborted) this.abortController = null;
      }
    }
  }

  customElements.define('search-recently-viewed', SearchRecentlyViewed);
}
